import { Copy, Mail, Check, Send, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Textarea } from "../ui/textarea";
import { REPLY_TEMPLATES, MAX_NOTE_LEN } from "../../lib/replyTemplates";
import { useReplyDraft } from "../../hooks/useReplyDraft";

const ActionButton = ({ testId, onClick, primary, children, disabled }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-base transition-colors disabled:opacity-60 ${
      primary ? "bg-cyan-600 font-semibold text-white hover:bg-cyan-700" : "border border-slate-300 bg-white text-slate-800 hover:border-cyan-500"
    }`}
  >
    {children}
  </button>
);

const TemplateTabs = ({ active, onPick }) => (
  <div className="mt-6 flex flex-wrap gap-2" data-testid="reply-template-tabs">
    {REPLY_TEMPLATES.map((t) => (
      <button
        key={t.id}
        type="button"
        data-testid={`reply-template-${t.id}`}
        onClick={() => onPick(t.id)}
        className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
          t.id === active ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-700 border-slate-300 hover:border-cyan-500"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const DraftPreview = ({ draft }) => (
  <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</div>
    <div data-testid="reply-subject" className="mt-1 text-base font-medium text-slate-900">{draft.subject}</div>
    <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Message preview</div>
    <pre data-testid="reply-body" className="mt-1 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-slate-800">{draft.body}</pre>
  </div>
);

const ReplyActions = ({ r }) => (
  <div className="mt-6 flex flex-wrap gap-3">
    <ActionButton testId="reply-send-button" onClick={r.send} primary disabled={r.sending}>
      {r.sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {r.sending ? "Sending…" : "Send from portal"}
    </ActionButton>
    <ActionButton testId="reply-copy-button" onClick={r.copy}>
      {r.copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {r.copied ? "Copied" : "Copy"}
    </ActionButton>
    <a
      data-testid="reply-mailto-button"
      href={r.mailtoHref}
      onClick={(e) => { e.preventDefault(); r.mailto(); }}
      className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-base transition-colors border border-slate-300 bg-white text-slate-800 hover:border-cyan-500"
    >
      <Mail className="w-4 h-4" /> Open in email
    </a>
  </div>
);

export const ReplyDrawer = ({ lead, open, onOpenChange, onStatus, onSend }) => {
  const r = useReplyDraft({ lead, onStatus, onSend, onClose: () => onOpenChange(false) });
  if (!lead) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="reply-drawer" className="w-full sm:max-w-xl overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className="text-slate-900">Reply to {lead.name}</SheetTitle>
          <SheetDescription className="text-slate-600">
            {lead.email} · {lead.reference}. Pick a draft, add a personal note, then send it from the portal or your own email app.
          </SheetDescription>
        </SheetHeader>
        <TemplateTabs active={r.templateId} onPick={r.pick} />
        <DraftPreview draft={r.draft} />
        <label htmlFor="reply-note" className="block mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Personal note (optional, added before the sign-off)
        </label>
        <Textarea
          id="reply-note"
          data-testid="reply-note-textarea"
          value={r.note}
          maxLength={MAX_NOTE_LEN}
          onChange={(e) => r.setNote(e.target.value)}
          rows={3}
          placeholder="e.g. I'll be in your area the week of the 14th if a site visit helps."
          className="mt-1.5 bg-white border-slate-300 text-base"
        />
        <ReplyActions r={r} />
        <p className="mt-3 text-sm text-slate-500" data-testid="reply-send-hint">
          Sent from the portal: goes to {lead.email} with a copy to your alert inbox, and is logged in the lead's activity.
          {r.willChangeStatus && <> Status will be set to <span className="font-medium text-slate-700">{r.template.status}</span>.</>}
        </p>
      </SheetContent>
    </Sheet>
  );
};
